import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { dataGridStyle } from "../../../theme";
import { GridToolbarBar, ToolbarDivider, ToolbarGroup, ToolIconButton } from "../../../components/ToolbarKit";

import {
  Box,
  Stack,
  Typography,
  useTheme,
  Popover,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  InputAdornment,
} from "@mui/material";

import { DataGridPro } from "@mui/x-data-grid-pro";

import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import API from "../../../api";
import EventsTimelineView from "./EventsTimelineView";
import { AckButton } from "../../../components/columns";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Europe/Rome";

const MAIN_GRID_HEIGHT = 520;

/*
 * Ordine personalizzato delle Severity.
 */
const SEVERITY_SORT_ORDER = {
  1: 0, // Critical
  0: 1, // High
  2: 2, // Alert
  3: 3, // Movement
};

/*
 * Converte un valore in stringa in maniera sicura.
 */
function safeStr(v) {
  if (
    v === null ||
    v === undefined
  ) {
    return "";
  }

  return String(v);
}

/*
 * Converte un valore in numero in maniera sicura.
 */
function safeNum(v, def = 0) {
  const n =
    typeof v === "string"
      ? Number(v)
      : v;

  return Number.isFinite(n)
    ? n
    : def;
}

/*
 * Formattazione timestamp.
 */
function formatTs(ts) {
  if (!ts) {
    return "-";
  }

  const d = dayjs(ts);

  return d.isValid()
    ? d
        .tz(TZ)
        .format("DD/MM/YYYY HH:mm:ss")
    : "-";
}

/*
 * Restituisce la label associata alla Severity.
 */
function getSeverityLabel(severity) {
  switch (
    safeNum(severity, -1)
  ) {
    case 0:
      return "High";

    case 1:
      return "Critical";

    case 2:
      return "Alert";

    case 3:
      return "Movement";

    default:
      return (
        safeStr(severity) || "-"
      );
  }
}

/*
 * Restituisce il valore usato per ordinare
 * le Severity.
 */
function getSeveritySortValue(
  severity
) {
  const sev = safeNum(
    severity,
    -1
  );

  return (
    SEVERITY_SORT_ORDER[sev] ??
    999
  );
}

/*
 * Normalizza gli eventi recuperati dall'API.
 */
function normalizeEvents(apiData) {
  const arr = Array.isArray(apiData)
    ? apiData
    : Array.isArray(apiData?.data)
      ? apiData.data
      : Array.isArray(apiData?.events)
        ? apiData.events
        : [];

  return arr
    .map((item, idx) => {
      const eventData =
        item?.eventData || {};

      return {
        id:
          safeStr(item?._id) ||
          `event-${idx}`,

        timestamp: item?.timestamp
          ? new Date(
              item.timestamp
            )
          : null,

        signature: safeStr(
          item?.signature
        ),

        severity: safeNum(
          item?.severity,
          -1
        ),

        severity_label:
          getSeverityLabel(
            item?.severity
          ),

        status: safeStr(
          item?.status
        ),

        description:
          safeStr(
            item?.description
          ) ||
          safeStr(
            item?.message
          ) ||
          safeStr(
            item?.notification
          ) ||
          safeStr(
            item?.event_name
          ) ||
          safeStr(
            item?.title
          ),

        src_ip:
          safeStr(
            item?.src_ip
          ) ||
          safeStr(
            item?.source_ip
          ) ||
          safeStr(
            eventData?.src_ip
          ) ||
          safeStr(
            eventData?.source_ip
          ),

        src_port:
          item?.src_port ??
          item?.source_port ??
          eventData?.src_port ??
          eventData?.source_port ??
          null,

        dest_ip:
          safeStr(
            item?.dest_ip
          ) ||
          safeStr(
            item?.destination_ip
          ) ||
          safeStr(
            item?.dst_ip
          ) ||
          safeStr(
            eventData?.dest_ip
          ) ||
          safeStr(
            eventData?.destination_ip
          ) ||
          safeStr(
            eventData?.dst_ip
          ),

        dest_port:
          item?.dest_port ??
          item?.destination_port ??
          item?.dst_port ??
          eventData?.dest_port ??
          eventData?.destination_port ??
          eventData?.dst_port ??
          null,

        /*
         * L3/L4 + raw capture.
         *
         * Necessari al PCAP exporter per
         * sintetizzare gli header di protocollo
         * quando è presente solamente il payload L7.
         */
        proto:
          safeStr(
            item?.proto
          ) ||
          safeStr(
            item?.protocol
          ) ||
          safeStr(
            eventData?.proto
          ),

        protocol:
          safeStr(
            item?.protocol
          ) ||
          safeStr(
            item?.proto
          ) ||
          safeStr(
            eventData?.protocol
          ),

        signature_id:
          item?.signature_id ??
          eventData?.signature_id ??
          null,

        event_id:
          safeStr(
            item?.event_id
          ) ||
          safeStr(
            eventData?.event_id
          ),

        acked:
          !!item?.acked,

        packet:
          item?.packet ||
          eventData?.packet ||
          null,

        payload:
          item?.payload ||
          eventData?.payload ||
          null,

        packet_info:
          item?.packet_info ||
          eventData?.packet_info ||
          null,

        raw: item,
      };
    })
    .filter(
      (item) =>
        item.timestamp &&
        !Number.isNaN(
          item.timestamp.getTime()
        )
    )
    .sort(
      (a, b) =>
        a.timestamp -
        b.timestamp
    );
}

const TableHosts = ({
  data = [],
  loading = false,
  setRarest = () => {},
  timeFilter,

  /*
   * Severity iniziale proveniente dalla URL.
   *
   * Esempi:
   *
   * []  -> nessun filtro
   * [0] -> High
   * [2] -> Alert
   */
  initialSeverity = [],

  onDetailsOpenChange,
}) => {
  const theme = useTheme();


  const paperBg =
    theme.palette.background.paper;

  const txtSecondary =
    theme.palette.text.secondary;

  const [
    rowEventsMap,
    setRowEventsMap,
  ] = useState({});

  const [
    rowLoadingMap,
    setRowLoadingMap,
  ] = useState({});

  const [
    rowErrorMap,
    setRowErrorMap,
  ] = useState({});

  /*
   * Override locale dello stato "acked" per riga (case-level),
   * applicato subito dopo il click sul bottone ACK senza
   * dover ricaricare i dati dal server.
   */
  const [
    ackOverrides,
    setAckOverrides,
  ] = useState({});

  /*
   * Nasconde le righe già ack-ate.
   */
  const [
    hideAcked,
    setHideAcked,
  ] = useState(false);

  /*
   * Filtri della tabella.
   *
   * La Severity viene inizializzata eventualmente
   * con il valore ricevuto dalla query string.
   */
  const [filter, setFilter] =
    useState(() => ({
      hosts: [],
      desc: "",
      severity: initialSeverity,
      aggType: [],
    }));

  const [
    filterAnchor,
    setFilterAnchor,
  ] = useState(null);

  const [
    openFilter,
    setOpenFilter,
  ] = useState(null);

  const [
    aggTypes,
    setAggTypes,
  ] = useState([]);

  /*
   * Recupero Aggregation Types.
   */
  useEffect(() => {
    API.getSystemConfig(
      "aggregations",
      "agg_types"
    )
      .then((res) => {
        setAggTypes(
          res?.data?.[0]?.value ??
            []
        );
      })
      .catch(() => {
        setAggTypes([]);
      });
  }, []);

  /*
   * Invalida la cache degli eventi
   * quando cambia il filtro temporale.
   */
  useEffect(() => {
    setRowEventsMap({});
    setRowErrorMap({});
  }, [timeFilter]);

  const [
    selectedRow,
    setSelectedRow,
  ] = useState(null);

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 100,
  });

  /*
   * Comunica al componente padre se
   * siamo entrati nel dettaglio.
   */
  useEffect(() => {
    if (
      typeof onDetailsOpenChange !==
      "function"
    ) {
      return;
    }

    onDetailsOpenChange(
      !!selectedRow
    );
  }, [
    selectedRow,
    onDetailsOpenChange,
  ]);

  /*
   * Normalizzazione righe.
   */
  const rows = useMemo(() => {
    const arr = Array.isArray(data)
      ? data
      : [];

    return arr.map(
      (item, idx) => {
        const rowId =
          safeStr(item?._id) ||
          safeStr(
            item?.case_id
          ) ||
          `row-${idx}`;

        // Chiave usata per l'ACK: va preferito "case_id" (identificativo
        // di business stabile) rispetto a "_id" (Mongo), perché il case
        // può essere ricalcolato/ricreato con un nuovo _id mantenendo lo
        // stesso case_id — usare _id farebbe "perdere" l'ack ad ogni
        // rigenerazione.
        const ackKey =
          safeStr(item?.case_id) ||
          rowId;

        return {
        id: rowId,

        event_id: ackKey,

        acked:
          ackOverrides[ackKey] ??
          !!item?.acked,

        raw: item,

        flow_ids:
          Array.isArray(
            item?.flow_ids
          )
            ? item.flow_ids
            : [],

        updated_at:
          item?.updated_at ||
          null,

        first_seen:
          item?.first_seen ||
          null,

        last_seen:
          item?.last_seen ||
          null,

        host:
          safeStr(
            item?.host
          ) ||
          safeStr(
            item?.group_values
              ?.host
          ) ||
          "-",

        group_by:
          Array.isArray(
            item?.group_by
          )
            ? item.group_by
            : [],

        description:
          safeStr(
            item?.description
          ),

        rule_type:
          safeStr(
            item?.rule_type
          ),

        rule_name:
          safeStr(
            item?.rule_name
          ),

        rule_id:
          safeStr(
            item?.rule_id
          ),

        severity:
          safeNum(
            item?.severity,
            -1
          ),

        severity_label:
          getSeverityLabel(
            item?.severity
          ),

        severity_sort:
          getSeveritySortValue(
            item?.severity
          ),

        status:
          safeStr(
            item?.status
          ),

        events_count:
          safeNum(
            item?.events_count
          ),
        };
      }
    );
  }, [data, ackOverrides]);

  /*
   * Lista Host disponibili.
   */
  const hostOptions =
    useMemo(
      () =>
        [
          ...new Set(
            rows
              .map(
                (r) =>
                  r.host
              )
              .filter(
                (h) =>
                  h &&
                  h !== "-"
              )
          ),
        ].sort(),
      [rows]
    );

  /*
   * Lista Severity disponibili.
   */
  const severityOptions =
    useMemo(() => {
      const seen =
        new Set();

      return rows
        .map((r) => ({
          value:
            r.severity,

          label:
            r.severity_label,
        }))
        .filter((s) => {
          if (
            seen.has(
              s.value
            )
          ) {
            return false;
          }

          seen.add(
            s.value
          );

          return true;
        })
        .sort(
          (a, b) =>
            getSeveritySortValue(
              a.value
            ) -
            getSeveritySortValue(
              b.value
            )
        );
    }, [rows]);

  /*
   * Applicazione filtri.
   */
  const filteredRows =
    useMemo(() => {
      const descQuery =
        filter.desc
          .trim()
          .toLowerCase();

      return rows.filter(
        (row) => {
          if (
            filter.hosts
              .length &&
            !filter.hosts.includes(
              row.host
            )
          ) {
            return false;
          }

          if (
            descQuery &&
            !row.description
              .toLowerCase()
              .includes(
                descQuery
              )
          ) {
            return false;
          }

          if (
            filter.severity
              .length &&
            !filter.severity.includes(
              row.severity
            )
          ) {
            return false;
          }

          if (
            filter.aggType
              .length &&
            !filter.aggType.includes(
              row.rule_id
            )
          ) {
            return false;
          }

          if (
            hideAcked &&
            row.acked
          ) {
            return false;
          }

          return true;
        }
      );
    }, [
      rows,
      filter,
      hideAcked,
    ]);

  /*
   * Apertura filtro.
   */
  const handleOpenFilter =
    useCallback(
      (field) =>
        (event) => {
          setFilterAnchor(
            event.currentTarget
          );

          setOpenFilter(
            field
          );
        },
      []
    );

  /*
   * Chiusura filtro.
   */
  const handleCloseFilter =
    useCallback(() => {
      setFilterAnchor(
        null
      );

      setOpenFilter(
        null
      );
    }, []);

  /*
   * Toggle filtri multipli.
   */
  const toggleListFilter =
    useCallback(
      (
        field,
        value
      ) => {
        setFilter(
          (prev) => {
            const current =
              prev[field];

            const next =
              current.includes(
                value
              )
                ? current.filter(
                    (v) =>
                      v !==
                      value
                  )
                : [
                    ...current,
                    value,
                  ];

            return {
              ...prev,
              [field]:
                next,
            };
          }
        );
      },
      []
    );

  /*
   * Pulizia singolo filtro.
   */
  const clearFilter =
    useCallback(
      (field) => {
        setFilter(
          (prev) => ({
            ...prev,

            [field]:
              Array.isArray(
                prev[field]
              )
                ? []
                : "",
          })
        );
      },
      []
    );

  /*
   * Recupero degli eventi associati alla riga.
   */
  const fetchRowEvents =
    useCallback(
      async (row) => {
        const rowId =
          row.id;

        const flowIds =
          Array.isArray(
            row.flow_ids
          )
            ? row.flow_ids
                .map((v) =>
                  safeStr(
                    v
                  ).trim()
                )
                .filter(
                  Boolean
                )
            : [];

        const flow_id =
          flowIds.join(",");

        if (!flow_id) {
          setRowEventsMap(
            (prev) => ({
              ...prev,
              [rowId]: [],
            })
          );

          setRowErrorMap(
            (prev) => ({
              ...prev,
              [rowId]: "",
            })
          );

          setRarest([]);

          return [];
        }

        setRowLoadingMap(
          (prev) => ({
            ...prev,
            [rowId]: true,
          })
        );

        setRowErrorMap(
          (prev) => ({
            ...prev,
            [rowId]: "",
          })
        );

        try {
          const res =
            await API.getSecurityEventsByFlow_id(
              {
                flow_id,
                timeFilter,
              }
            );

          const normalizedEvents =
            normalizeEvents(
              res?.data
            );

          setRowEventsMap(
            (prev) => ({
              ...prev,
              [rowId]:
                normalizedEvents,
            })
          );

          setRarest(
            normalizedEvents
          );

          return normalizedEvents;
        } catch (error) {
          console.error(
            "[fetchRowEvents] getSecurityEventsByFlow_id error:",
            error
          );

          setRowErrorMap(
            (prev) => ({
              ...prev,

              [rowId]:
                "Errore durante il recupero degli eventi.",
            })
          );

          setRowEventsMap(
            (prev) => ({
              ...prev,
              [rowId]: [],
            })
          );

          setRarest([]);

          return [];
        } finally {
          setRowLoadingMap(
            (prev) => ({
              ...prev,
              [rowId]:
                false,
            })
          );
        }
      },
      [
        setRarest,
        timeFilter,
      ]
    );

  /*
   * ACK/unACK di un case aggregato (riga della tabella).
   */
  const handleAckCase =
    useCallback(
      async (row, shouldAck) => {
        if (!row.event_id) return;
        try {
          await API.setAckEvent({
            event_id: row.event_id,
            set: shouldAck ? 1 : 0,
            timestamp: row.updated_at,
            severity: row.severity,
            type: "aggregation",
          });
          setAckOverrides((prev) => ({
            ...prev,
            [row.event_id]: shouldAck,
          }));
        } catch {
          // silent fail — coerente con dataGridSecurity, il bottone torna
          // allo stato precedente al successivo refresh dei dati
        }
      },
      []
    );

  /*
   * ACK/unACK di un singolo evento nel drill-down (Crono).
   */
  const handleAckEvent =
    useCallback(
      async (row, shouldAck) => {
        if (!row.event_id) return;
        try {
          await API.setAckEvent({
            event_id: row.event_id,
            set: shouldAck ? 1 : 0,
            timestamp: row.timestamp,
            severity: row.severity,
            type: "aggregation",
          });
          setRowEventsMap((prev) => {
            const rowId = selectedRow?.id;
            if (!rowId || !prev[rowId]) return prev;
            return {
              ...prev,
              [rowId]: prev[rowId].map((e) =>
                e.id === row.id
                  ? { ...e, acked: shouldAck }
                  : e
              ),
            };
          });
        } catch {
          // silent fail — coerente con dataGridSecurity
        }
      },
      [selectedRow]
    );

  /*
   * Click su una riga.
   */
  const handleRowClick =
    useCallback(
      async (params) => {
        const row =
          params.row;

        const rowId =
          row.id;

        setSelectedRow(
          row
        );

        if (
          !Object.prototype.hasOwnProperty.call(
            rowEventsMap,
            rowId
          )
        ) {
          await fetchRowEvents(
            row
          );
        }
      },
      [
        rowEventsMap,
        fetchRowEvents,
      ]
    );

  /*
   * Torna dalla Timeline alla tabella.
   */
  const handleBack =
    useCallback(() => {
      setSelectedRow(
        null
      );
    }, []);

  /*
   * Colonne DataGrid.
   */
  const columns = useMemo(
    () => [
      {
        field: "ack",
        headerName: "ACK",
        width: 64,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <AckButton
            row={params.row}
            onAck={handleAckCase}
          />
        ),
      },

      {
        field:
          "updated_at",

        headerName:
          "Timestamp",

        width: 190,

        renderCell: (
          params
        ) =>
          formatTs(
            params.row
              .updated_at
          ),
      },

      {
        field: "host",
        headerName:
          "Host",
        width: 170,
      },

      {
        field:
          "description",

        headerName:
          "Description",

        flex: 1,

        minWidth: 320,
      },

      {
        field:
          "severity",

        headerName:
          "Severity",

        width: 130,

        type: "number",

        valueGetter: (
          _,
          row
        ) =>
          row?.severity_sort ??
          999,

        renderCell: (
          params
        ) =>
          params.row
            .severity_label,
      },
    ],
    [handleAckCase]
  );

  /*
   * Stile delle checkbox.
   */
  const checkboxLabelSx = {
    alignItems:
      "flex-start",

    ml: 0,
    mr: 0,

    "& .MuiCheckbox-root":
      {
        pt: "3px",
      },

    "& .MuiFormControlLabel-label":
      {
        whiteSpace:
          "normal",

        wordBreak:
          "break-word",

        fontSize:
          "0.875rem",
      },
  };

  /*
   * Stile DataGrid.
   */
  const mainGridSx = useMemo(
    () => ({
      ...dataGridStyle,
      border: "none",
      borderRadius: 0,
      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
          {
            outline: "none",
          },

        "& .MuiDataGrid-row":
          {
            cursor:
              "pointer",
          },
      }),
      []
    );

  /*
   * Visualizzazione Timeline.
   */
  if (selectedRow) {
    const selectedRowId =
      selectedRow.id;

    return (
      <EventsTimelineView
        row={selectedRow}
        events={
          rowEventsMap[
            selectedRowId
          ] || []
        }
        loading={
          !!rowLoadingMap[
            selectedRowId
          ]
        }
        error={
          rowErrorMap[
            selectedRowId
          ] || ""
        }
        onBack={
          handleBack
        }
        onAckEvent={
          handleAckEvent
        }
      />
    );
  }

  return (
    <Box>
      <Box
        mt={3}
        p={0}
        sx={{
          borderRadius: 2,

          border:
            "1px solid",

          borderColor:
            "divider",

          overflow:
            "hidden",

          backgroundColor:
            paperBg,
        }}
      >
        <GridToolbarBar>
          <ToolbarGroup>
            <ToolIconButton title="Filter by host" dot active={filter.hosts.length > 0} onClick={handleOpenFilter("hosts")}>
              <DnsOutlinedIcon fontSize="small" />
            </ToolIconButton>
            <ToolIconButton title="Filter by description" dot active={filter.desc.trim() !== ""} onClick={handleOpenFilter("desc")}>
              <DescriptionOutlinedIcon fontSize="small" />
            </ToolIconButton>
            <ToolIconButton title="Filter by severity" dot active={filter.severity.length > 0} onClick={handleOpenFilter("severity")}>
              <WarningAmberOutlinedIcon fontSize="small" />
            </ToolIconButton>
            <ToolIconButton title="Filter by aggregation type" dot active={filter.aggType.length > 0} onClick={handleOpenFilter("aggType")}>
              <CategoryOutlinedIcon fontSize="small" />
            </ToolIconButton>
          </ToolbarGroup>
          <ToolbarDivider />
          <ToolbarGroup>
            <ToolIconButton
              title={hideAcked ? "Show acknowledged" : "Hide acknowledged"}
              active={hideAcked}
              onClick={() => setHideAcked((prev) => !prev)}
            >
              {hideAcked ? <CheckCircleOutlineIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
            </ToolIconButton>
          </ToolbarGroup>
        </GridToolbarBar>

        <Popover
          open={
            !!openFilter
          }
          anchorEl={
            filterAnchor
          }
          onClose={
            handleCloseFilter
          }
          anchorOrigin={{
            vertical:
              "bottom",

            horizontal:
              "right",
          }}
          transformOrigin={{
            vertical:
              "top",

            horizontal:
              "right",
          }}
          slotProps={{
            paper: {
              sx: {
                maxHeight:
                  "80vh",

                overflowY:
                  "auto",
              },
            },
          }}
        >
          <Box
            sx={{
              p: 2,

              width: 280,

              maxWidth:
                "90vw",

              overflowX:
                "hidden",
            }}
          >
            {/* HOST FILTER */}
            {openFilter ===
              "hosts" && (
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={
                    700
                  }
                >
                  Host
                </Typography>

                <FormGroup
                  sx={{
                    maxHeight:
                      260,

                    overflowY:
                      "auto",

                    overflowX:
                      "hidden",
                  }}
                >
                  {hostOptions.length ===
                    0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          txtSecondary,
                      }}
                    >
                      No hosts
                      available
                    </Typography>
                  )}

                  {hostOptions.map(
                    (host) => (
                      <FormControlLabel
                        key={
                          host
                        }
                        sx={
                          checkboxLabelSx
                        }
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.hosts.includes(
                              host
                            )}
                            onChange={() =>
                              toggleListFilter(
                                "hosts",
                                host
                              )
                            }
                          />
                        }
                        label={
                          host
                        }
                      />
                    )
                  )}
                </FormGroup>

                <Button
                  size="small"
                  onClick={() =>
                    clearFilter(
                      "hosts"
                    )
                  }
                >
                  Clear
                </Button>
              </Stack>
            )}

            {/* DESCRIPTION FILTER */}
            {openFilter ===
              "desc" && (
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={
                    700
                  }
                >
                  Description
                </Typography>

                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search description..."
                  value={
                    filter.desc
                  }
                  onChange={(
                    e
                  ) =>
                    setFilter(
                      (
                        prev
                      ) => ({
                        ...prev,

                        desc:
                          e
                            .target
                            .value,
                      })
                    )
                  }
                  InputProps={{
                    startAdornment:
                      (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                  }}
                />

                <Button
                  size="small"
                  onClick={() =>
                    clearFilter(
                      "desc"
                    )
                  }
                >
                  Clear
                </Button>
              </Stack>
            )}

            {/* SEVERITY FILTER */}
            {openFilter ===
              "severity" && (
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={
                    700
                  }
                >
                  Severity
                </Typography>

                <FormGroup
                  sx={{
                    overflowX:
                      "hidden",
                  }}
                >
                  {severityOptions.map(
                    (opt) => (
                      <FormControlLabel
                        key={
                          opt.value
                        }
                        sx={
                          checkboxLabelSx
                        }
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.severity.includes(
                              opt.value
                            )}
                            onChange={() =>
                              toggleListFilter(
                                "severity",
                                opt.value
                              )
                            }
                          />
                        }
                        label={
                          opt.label
                        }
                      />
                    )
                  )}
                </FormGroup>

                <Button
                  size="small"
                  onClick={() =>
                    clearFilter(
                      "severity"
                    )
                  }
                >
                  Clear
                </Button>
              </Stack>
            )}

            {/* AGGREGATION TYPE FILTER */}
            {openFilter ===
              "aggType" && (
              <Stack spacing={1}>
                <Typography
                  variant="subtitle2"
                  fontWeight={
                    700
                  }
                >
                  Aggregation
                  type
                </Typography>

                <FormGroup
                  sx={{
                    overflowX:
                      "hidden",
                  }}
                >
                  {aggTypes.length ===
                    0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          txtSecondary,
                      }}
                    >
                      No aggregation
                      types available
                    </Typography>
                  )}

                  {aggTypes.map(
                    (
                      aggType
                    ) => (
                      <FormControlLabel
                        key={
                          aggType.id
                        }
                        sx={
                          checkboxLabelSx
                        }
                        control={
                          <Checkbox
                            size="small"
                            checked={filter.aggType.includes(
                              aggType.id
                            )}
                            onChange={() =>
                              toggleListFilter(
                                "aggType",
                                aggType.id
                              )
                            }
                          />
                        }
                        label={
                          aggType.label
                        }
                      />
                    )
                  )}
                </FormGroup>

                <Button
                  size="small"
                  onClick={() =>
                    clearFilter(
                      "aggType"
                    )
                  }
                >
                  Clear
                </Button>
              </Stack>
            )}
          </Box>
        </Popover>

        <Box
          sx={{
            height:
              MAIN_GRID_HEIGHT,

            minHeight:
              320,
          }}
        >
          <DataGridPro
            loading={loading}
            slotProps={{ loadingOverlay: { variant: "skeleton" } }}
            rows={
              filteredRows
            }
            columns={
              columns
            }
            density="compact"
            disableRowSelectionOnClick
            onRowClick={
              handleRowClick
            }
            pagination
            paginationMode="client"
            paginationModel={
              paginationModel
            }
            onPaginationModelChange={
              setPaginationModel
            }
            pageSizeOptions={[
              10,
              25,
              50,
              100,
            ]}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field:
                      "last_seen",
                    sort:
                      "desc",
                  },
                ],
              },
            }}
            sx={
              mainGridSx
            }
            localeText={{
              noRowsLabel:
                "No data found",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TableHosts;